// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a customized autocomplete component for location selection.
 * It extends Material-UI's Autocomplete component with location-specific rendering
 * and behavior, including support for archived and transient locations.
 */
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { Location } from '@/location/api/types';
import { isZoneOption } from '@/location/components/location-search-options';
import { VirtualizedListbox } from '@/location/components/location-virtualized-listbox';
import { SiteAddressLabel } from '@/location/components/site-address-label';
import {
  Autocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { omit } from 'lodash-es';
import { ForwardedRef, HTMLAttributes, ReactNode, forwardRef } from 'react';

/**
 * Subset of Location properties required for the autocomplete component.
 * Minimizes data requirements while maintaining necessary functionality.
 */
type LocationOption = Pick<
  Location,
  | 'id'
  | 'name'
  | 'address'
  | 'isArchived'
  | 'isTransient'
  | 'isThirdPartyEnabled'
>;

/**
 * Special ID used to identify the "create new location" option in the dropdown.
 * This option appears when the user's input doesn't match any existing locations.
 */
export const CREATE_LOCATION_OPTION_ID = 'create-location';

/**
 * Determines if two location options represent the same location.
 * Used by the Autocomplete component for selection handling.
 *
 * @param option - First location option to compare
 * @param value - Second location option to compare
 * @returns true if the locations have the same ID
 */
function isOptionEqualToValue(option: LocationOption, value: LocationOption) {
  return option.id === value.id;
}

interface ApiZoneValue extends LocationOption {
  type: 'ZONE';
  parentLocation: { name: string };
}

function isApiZoneValue(option: LocationOption): option is ApiZoneValue {
  return (
    'type' in option &&
    'parentLocation' in option &&
    (option as ApiZoneValue).type === 'ZONE' &&
    (option as ApiZoneValue).parentLocation != null
  );
}

interface SiteWithZonesValue extends LocationOption {
  zones: readonly { id: string }[];
}

function isSiteWithZones(option: LocationOption): option is SiteWithZonesValue {
  if (!('zones' in option)) return false;
  const zones = (option as SiteWithZonesValue).zones;
  if (!Array.isArray(zones) || zones.length === 0) return false;
  // Named zones inherit the parent's zones array but their own id matches a
  // zone entry. The default zone's id is the parent site id, which never
  // appears in the zones array.
  const isNamedZone = zones.some((zone) => zone.id === option.id);
  return !isNamedZone;
}

function getOptionLabel<FreeSolo extends boolean | undefined>(
  option: LocationOption | AutocompleteFreeSoloValueMapping<FreeSolo>
) {
  if (typeof option === 'string') {
    return option;
  }

  // Zone options from the search dropdown carry _parentName / _zoneName metadata
  if (isZoneOption(option)) {
    return `${option._parentName} \u2014 ${option._zoneName}`;
  }

  // Zone values from the API carry type + parentLocation
  if (isApiZoneValue(option)) {
    return `${option.parentLocation.name} \u2014 ${option.name}`;
  }

  // Default zone: site with active zones (form value after metadata stripped, or API site)
  if (isSiteWithZones(option)) {
    return `${option.name} \u2014 Default`;
  }

  return option.name;
}

/**
 * Renders selected location tags in multiple selection mode.
 * Creates Chip components for each selected location, preserving
 * accessibility and interaction props.
 *
 * @param value - Array of selected locations
 * @param getTagProps - Callback to get props for each tag
 * @returns Array of Chip components representing selected locations
 */
function renderTags(
  value: LocationOption[],
  getTagProps: AutocompleteRenderGetTagProps
) {
  return value.map((option, index) => {
    const props = omit(getTagProps({ index }), 'key');

    return <Chip key={option.id} label={getOptionLabel(option)} {...props} />;
  });
}

/**
 * Creates a render function for a single option in the autocomplete dropdown.
 * Provides specialized rendering for different location types:
 * - Create new location option: Shows add icon and highlighted text
 * - Archived locations: Displays "(Archived)" suffix and muted text
 * - Transient locations: Displays "(Transient)" suffix and muted text
 * - Regular locations: Shows name and address in standard styling
 * - Non-priority locations: Displays in italic when priorityLocationIds is provided
 *
 * @param priorityLocationIds - Optional array of location IDs in the priority group
 * @returns Render function for dropdown options
 */
function createRenderOption(priorityLocationIds?: string[]) {
  return (
    attributes: HTMLAttributes<HTMLLIElement>,
    option: LocationOption
  ) => {
    // Prevent 'key' prop from being spread to DOM element
    const props = omit(attributes, 'key');

    // Special rendering for "create new location" option
    if (option.id === CREATE_LOCATION_OPTION_ID) {
      return (
        <li {...props} key={option.id} data-option-type="create">
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddRounded fontSize="small" color="primary" />
            <Typography color="primary.main">
              Add location <b>{option.name}</b>
            </Typography>
          </Stack>
        </li>
      );
    }

    // Zone option rendering (parent headers and child zones)
    if (isZoneOption(option)) {
      // Zone parent: looks like a regular location but not selectable
      if (option._isZoneParent) {
        return (
          <li
            {...props}
            key={option.id}
            aria-label={option.name}
            data-option-type="zone-parent"
            style={{ ...props.style, opacity: 1, cursor: 'default' }}
          >
            <Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ lineHeight: 1, fontWeight: 600 }}
                >
                  {option.name}
                </Typography>
                {option.isThirdPartyEnabled && (
                  <Chip label="3PL" size="small" />
                )}
              </Stack>
              <SiteAddressLabel
                variant="body2"
                color="text.primary"
                site={option}
              />
            </Stack>
          </li>
        );
      }

      // Zone child (default zone and named zones)
      const displayName = option._isDefaultZone ? 'Default' : option._zoneName;
      const isArchived = option.isArchived;
      const zoneLabel = isArchived ? `${displayName} (Archived)` : displayName;
      const zoneColor = isArchived ? 'text.secondary' : 'text.primary';

      return (
        <li
          {...props}
          key={option.id}
          aria-label={zoneLabel}
          data-option-type="zone-child"
        >
          <Stack sx={{ pl: 2, py: 0.5 }}>
            <Typography
              variant="subtitle2"
              color={zoneColor}
              sx={{ lineHeight: 1 }}
            >
              → zone: {zoneLabel}
            </Typography>
          </Stack>
        </li>
      );
    }

    // Determine display style based on location status
    let color = 'text.primary';
    let label = option.name;

    if (option.isArchived) {
      label = `${option.name} (Archived)`;
      color = 'text.secondary';
    }

    if (option.isTransient) {
      label = `${option.name} (Transient)`;
      color = 'text.secondary';
    }

    // Check if this location should be displayed in italic (non-priority)
    const isNonPriority =
      priorityLocationIds &&
      priorityLocationIds.length > 0 &&
      !priorityLocationIds.includes(option.id);

    return (
      <li
        {...props}
        key={option.id}
        aria-label={label}
        data-option-type="regular"
      >
        <Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle2"
              color={color}
              sx={{
                lineHeight: 1,
                fontStyle: isNonPriority ? 'italic' : 'normal',
              }}
            >
              {label}
            </Typography>
            {option.isThirdPartyEnabled && <Chip label="3PL" size="small" />}
          </Stack>
          <SiteAddressLabel
            variant="body2"
            color={color}
            site={option}
            sx={{ fontStyle: isNonPriority ? 'italic' : 'normal' }}
          />
        </Stack>
      </li>
    );
  };
}

/**
 * Props for the LocationAutocomplete component.
 * Extends Material-UI's Autocomplete props while adding custom form-related props.
 */
interface Props<
  Option extends LocationOption,
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<
    AutocompleteProps<Option, Multiple, false, FreeSolo>,
    | 'getOptionLabel'
    | 'isOptionEqualToValue'
    | 'renderInput'
    | 'renderOption'
    | 'renderTags'
  > {
  /**
   * Name attribute for the input element.
   * Used for form integration.
   */
  name?: string;

  /**
   * Label text displayed above the input.
   */
  label?: string;

  /**
   * Whether the component should display in an error state.
   */
  error?: boolean;

  /**
   * Helper text displayed below the input. Accepts a ReactNode so callers can
   * render inline indicators (e.g. an out-of-sync chip) alongside the text.
   */
  helperText?: ReactNode;

  /**
   * Placeholder text displayed when the input is empty.
   */
  placeholder?: string;

  /**
   * Optional array of location IDs that are in the priority group.
   * Locations not in this array will be displayed with italic styling.
   */
  priorityLocationIds?: string[];

  /**
   * If provided, sets a minimum width for the dropdown popper.
   * Useful when the input field is narrow but options need more space.
   */
  popperWidth?: number;
}

/**
 * A specialized autocomplete component for selecting locations.
 * Extends Material-UI's Autocomplete with location-specific rendering and behavior.
 *
 * Features:
 * - Single or multiple location selection
 * - Support for creating new locations
 * - Visual indicators for archived and transient locations
 * - Address display in dropdown options
 * - Form integration capabilities
 * - Ref forwarding to input element
 *
 * The component handles various location states and provides appropriate visual feedback:
 * - Regular locations show name and address
 * - Archived locations are displayed with "(Archived)" suffix and muted styling
 * - Transient locations show "(Transient)" suffix and muted styling
 * - Non-matching input creates a "Add location" option
 *
 * @example
 * ```tsx
 * // Single selection
 * <LocationAutocomplete
 *   label="Select location"
 *   options={locationsList}
 *   onChange={handleLocationChange}
 * />
 *
 * // Multiple selection
 * <LocationAutocomplete
 *   multiple
 *   label="Select locations"
 *   options={locationsList}
 *   onChange={handleLocationsChange}
 * />
 *
 * // With form integration
 * <LocationAutocomplete
 *   name="location"
 *   label="Location"
 *   error={!!formErrors.location}
 *   helperText={formErrors.location?.message}
 *   options={locationsList}
 *   onChange={handleLocationChange}
 * />
 * ```
 */
function LocationAutocompleteRenderer<
  Option extends LocationOption,
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: Props<Option, Multiple, FreeSolo>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const {
    name,
    label,
    placeholder,
    error,
    helperText,
    priorityLocationIds,
    popperWidth,
    ...autocompleteProps
  } = props;

  /**
   * Renders the input field component with form integration props.
   * Forwards the ref to enable focus management and form library integration.
   */
  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        onBlur={(event) => {
          if (event.target) {
            event.target.setSelectionRange(0, 0);
          }
        }}
        {...parameters}
      />
    );
  };

  // Create the renderOption function with access to priorityLocationIds
  const renderOption = createRenderOption(priorityLocationIds);

  return (
    <Autocomplete
      {...autocompleteProps}
      ListboxComponent={VirtualizedListbox}
      renderTags={renderTags}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      componentsProps={
        popperWidth
          ? {
              popper: {
                placement: 'bottom-start',
                sx: { minWidth: popperWidth },
              },
            }
          : undefined
      }
    />
  );
}

/**
 * A forwarded ref version of the LocationAutocomplete component.
 * This is the main export that should be used by other components.
 */
export const LocationAutocomplete = forwardRef(LocationAutocompleteRenderer);
